import { useId } from 'react';

/**
 * The InertiaID mark (since 2026-10-25): a shield with an inset border, holding a scan frame around
 * an "i" with a cyan dot. Scanning and verifying, inside security. Drawn inline so it needs no
 * asset request and follows the page's colours. Two static copies must stay in step with it:
 * `public/logo.svg` (this drawing above 24px; the source of the `logo-{32..512}.png` files the
 * emails use) and `public/favicon.svg` (the flat drawing, for the tab).
 *
 * At 24px and below the shadow, gloss and glow are left out: at that size they only blur the shape.
 */
export function BrandMark({ size = 40, className }: { size?: number; className?: string }) {
  // Gradient and filter ids must be unique per instance, and React's ":r1:" form is not a valid
  // url(#...) target everywhere, so the colons go.
  const uid = useId().replace(/:/g, '');
  const id = (name: string) => `${name}-${uid}`;
  const url = (name: string) => `url(#${id(name)})`;
  const rich = size > 24;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      style={{ overflow: 'visible', flexShrink: 0 }}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={id('body')} x1=".2" y1="0" x2=".8" y2="1">
          <stop offset="0" stopColor="#6366f1" />
          <stop offset=".6" stopColor="#4338ca" />
          <stop offset="1" stopColor="#312e81" />
        </linearGradient>
        <linearGradient id={id('line')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#e0e7ff" stopOpacity=".9" />
          <stop offset="1" stopColor="#e0e7ff" stopOpacity=".15" />
        </linearGradient>
        <linearGradient id={id('mark')} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="1" stopColor="#e0e7ff" />
        </linearGradient>
        {rich && (
          <>
            <linearGradient id={id('gloss')} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#ffffff" stopOpacity=".35" />
              <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
            </linearGradient>
            <filter id={id('drop')} x="-30%" y="-20%" width="160%" height="170%">
              <feDropShadow dx="0" dy="1.5" stdDeviation="1.5" floodColor="#4f46e5" floodOpacity=".35" />
            </filter>
            <filter id={id('glow')} x="-100%" y="-100%" width="300%" height="300%">
              <feGaussianBlur stdDeviation="1.6" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </>
        )}
      </defs>

      <path
        d="M32 2.5 56 10.8v18.6c0 15.8-10 27.6-24 32.6C18 57 8 45.2 8 29.4V10.8z"
        fill={url('body')}
        filter={rich ? url('drop') : undefined}
      />
      {rich && (
        <path
          d="M32 2.5 56 10.8v12.4C46 26.5 39 27.5 32 27.5s-14-1-24-4.3V10.8z"
          fill={url('gloss')}
          opacity=".7"
        />
      )}
      <path
        d="M32 7.3 51.5 14v15.4c0 13.2-8.2 23.1-19.5 27.5C20.7 52.5 12.5 42.6 12.5 29.4V14z"
        fill="none"
        stroke={url('line')}
        strokeWidth="1.5"
      />
      <g fill="none" stroke={url('mark')} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 24v-4a3.5 3.5 0 0 1 3.5-3.5H27" />
        <path d="M37 16.5h3.5A3.5 3.5 0 0 1 44 20v4" />
        <path d="M44 35v4a3.5 3.5 0 0 1-3.5 3.5H37" />
        <path d="M27 42.5h-3.5A3.5 3.5 0 0 1 20 39v-4" />
      </g>
      <circle cx="32" cy="23.5" r="3.4" fill="#22d3ee" filter={rich ? url('glow') : undefined} />
      <rect x="29" y="29.3" width="6" height="11.5" rx="3" fill={url('mark')} />
    </svg>
  );
}

/**
 * Mark and wordmark: "Inertia" in the text colour and "ID" in indigo. `onDark` is for dark
 * surfaces (the sidebar), where the text is white and "ID" a light indigo.
 */
export function BrandLogo({
  size = 40,
  onDark = false,
  textScale = 0.58,
}: {
  size?: number;
  onDark?: boolean;
  /** Wordmark height as a share of the mark's; the sign-in panel sets it larger. */
  textScale?: number;
}) {
  return (
    <span className={`brand-logo d-inline-flex align-items-center ${onDark ? 'brand-logo-on-dark' : ''}`} style={{ gap: size * 0.25 }}>
      <BrandMark size={size} />
      <span className="brand-wordmark" style={{ fontSize: Math.round(size * textScale) }}>
        Inertia<span className="brand-wordmark-id">ID</span>
      </span>
    </span>
  );
}
